import React from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Stack
} from '@mui/material';
import { 
  CollectionsOutlined, 
  ImageOutlined, 
  LayersOutlined, 
  CloudUploadOutlined, 
  CheckCircleOutline,
  ArrowForwardIos,
  MenuBook
} from '@mui/icons-material';

interface HomePageProps {
  onGetStarted: () => void;
  onTutorial: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ onGetStarted, onTutorial }) => {
  const theme = useTheme();

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          bgcolor: 'background.paper', 
          pt: 12,
          pb: 6,
          background: `linear-gradient(to right, ${theme.palette.background.default}, ${theme.palette.primary.dark})`,
          borderRadius: '0 0 20px 20px',
          boxShadow: 3,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at top right, rgba(3, 218, 198, 0.1), transparent 70%)',
            zIndex: 0
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                component="h1"
                variant="h2"
                color="white"
                fontWeight="bold"
                gutterBottom
                sx={{
                  background: `linear-gradient(90deg, #ffffff, ${theme.palette.secondary.light})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 3
                }}
              >
                NFT Art Engine
              </Typography>
              <Typography variant="h5" color="white" paragraph>
                Create your NFT collection easily with our powerful generation tool.
                Design, customize, and export your collection with metadata compatible with OpenSea.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
                <Button 
                  variant="contained" 
                  size="large" 
                  onClick={onGetStarted}
                  sx={{ 
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    background: theme.palette.secondary.main,
                    '&:hover': {
                      background: theme.palette.secondary.dark,
                    }
                  }}
                  endIcon={<ArrowForwardIos />}
                >
                  Get Started
                </Button>
                <Button 
                  variant="outlined" 
                  size="large" 
                  onClick={onTutorial}
                  sx={{ 
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: theme.palette.secondary.light,
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    }
                  }}
                  startIcon={<MenuBook />}
                >
                  Tutorial
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box 
                component="img"
                src="/nft-preview.png"
                alt="NFT Collection Preview"
                sx={{
                  width: '100%',
                  maxWidth: 400,
                  height: 'auto',
                  borderRadius: 4,
                  boxShadow: 4,
                  display: { xs: 'none', md: 'block' },
                  mx: 'auto'
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Container sx={{ py: 8 }} maxWidth="lg">
        <Typography variant="h3" align="center" gutterBottom fontWeight="bold">
          How It Works
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" paragraph>
          Create your NFT collection in just a few simple steps
        </Typography>
        
        <Grid container spacing={4} sx={{ mt: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3 }}>
              <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                <Box sx={{ mb: 2, color: theme.palette.primary.main }}>
                  <CollectionsOutlined sx={{ fontSize: 60 }} />
                </Box>
                <Typography gutterBottom variant="h5" component="h2" fontWeight="bold">
                  1. Configure
                </Typography>
                <Typography>
                  Set up your collection parameters, including size, name, and description.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3 }}>
              <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                <Box sx={{ mb: 2, color: theme.palette.primary.main }}>
                  <LayersOutlined sx={{ fontSize: 60 }} />
                </Box>
                <Typography gutterBottom variant="h5" component="h2" fontWeight="bold">
                  2. Create Layers
                </Typography>
                <Typography>
                  Define your trait layers and their rarity settings.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3 }}>
              <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                <Box sx={{ mb: 2, color: theme.palette.primary.main }}>
                  <ImageOutlined sx={{ fontSize: 60 }} />
                </Box>
                <Typography gutterBottom variant="h5" component="h2" fontWeight="bold">
                  3. Upload Images
                </Typography>
                <Typography>
                  Upload your trait images for each layer and add unique NFTs.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3 }}>
              <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                <Box sx={{ mb: 2, color: theme.palette.primary.main }}>
                  <CloudUploadOutlined sx={{ fontSize: 60 }} />
                </Box>
                <Typography gutterBottom variant="h5" component="h2" fontWeight="bold">
                  4. Generate
                </Typography>
                <Typography>
                  Generate your collection and download the images with metadata.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Features Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom fontWeight="bold">
            Features
          </Typography>
          
          <Grid container spacing={4} sx={{ mt: 4 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 4, height: '100%', borderRadius: 3 }}>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  Powerful Generation Engine
                </Typography>
                <List>
                  {[
                    'Layer-based trait generation with rarity controls',
                    'Support for unique 1/1 NFTs in your collection',
                    'Preview your NFTs before generation',
                    'Automatic collision detection to avoid duplicates',
                    'Batch processing for large collections'
                  ].map((text, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutline color="secondary" />
                      </ListItemIcon>
                      <ListItemText primary={text} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ p: 4, height: '100%', borderRadius: 3 }}>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  OpenSea Compatible
                </Typography>
                <List>
                  {[
                    'ERC-721 and ERC-1155 metadata standard support',
                    'Automatic trait rarity calculation',
                    'Customizable metadata fields',
                    'Ready for direct upload to IPFS',
                    'Seamless integration with OpenSea marketplace'
                  ].map((text, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutline color="secondary" />
                      </ListItemIcon>
                      <ListItemText primary={text} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Container sx={{ py: 8 }} maxWidth="lg">
        <Typography variant="h3" align="center" gutterBottom fontWeight="bold">
          Pricing
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" paragraph>
          Affordable options for creators of all sizes
        </Typography>
        
        <Paper 
          elevation={4} 
          sx={{ 
            p: 4, 
            mt: 4, 
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.primary.dark})`
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom fontWeight="bold" color="white">
                Beta Pricing
              </Typography>
              <Typography variant="body1" paragraph color="white">
                Currently <strong>FREE</strong> during our beta period!
              </Typography>
              <Divider sx={{ my: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h5" gutterBottom fontWeight="bold" color="white">
                Launch Pricing
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 2 }}>
                    <Typography variant="h6" color="white">Free</Typography>
                    <Typography variant="body2" color="white" sx={{ mb: 1 }}>
                      Up to 10 NFTs
                    </Typography>
                    <Typography variant="caption" color="white" sx={{ fontSize: '0.7rem', display: 'block' }}>
                      Try the app before creating your final collection
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 2 }}>
                    <Typography variant="h6" color="white">0.001 ETH</Typography>
                    <Typography variant="body2" color="white" sx={{ mb: 1 }}>
                      11-100 NFTs
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 2 }}>
                    <Typography variant="h6" color="white">0.003 ETH</Typography>
                    <Typography variant="body2" color="white" sx={{ mb: 1 }}>
                      101-500 NFTs
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 2 }}>
                    <Typography variant="h6" color="white">0.01 ETH</Typography>
                    <Typography variant="body2" color="white" sx={{ mb: 1 }}>
                      501-1000 NFTs
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 2, mt: 1 }}>
                    <Typography variant="h6" color="white">0.03 ETH</Typography>
                    <Typography variant="body2" color="white" sx={{ mb: 1 }}>
                      1000+ NFTs
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* CTA Section */}
      <Box 
        sx={{ 
          bgcolor: theme.palette.primary.main, 
          py: 6,
          borderRadius: '20px 20px 0 0',
          mt: 4
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" align="center" color="white" gutterBottom fontWeight="bold">
            Ready to create your NFT collection?
          </Typography>
          <Typography variant="h6" align="center" color="white" paragraph>
            Start building your unique NFT collection today with our easy-to-use tool.
          </Typography>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button 
                variant="contained" 
                size="large" 
                onClick={onGetStarted}
                sx={{ 
                  px: 6,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 'bold',
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)',
                  }
                }}
              >
                Get Started Now
              </Button>
              <Button 
                variant="outlined" 
                size="large" 
                onClick={onTutorial}
                sx={{ 
                  px: 6,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 'bold',
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.9)',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  }
                }}
                startIcon={<MenuBook />}
              >
                View Tutorial
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage; 